import type { IncidentRuleUsed } from '~/app/dashboard/components/incidentRulesUsed.utils';
/**
 * Hardcoded mock data for the EV charging "Day in the Life" demo.
 * Bypasses the backend entirely — no API calls, no DB.
 */

import type {
  BlockingReason,
  IncidentStatus,
  ResolutionType,
} from '@evimo/types';
import {
  getIncidentStatusGroup,
  isOpenIncidentStatus,
  openIncidentStatuses,
  resolvedIncidentStatuses,
  STATUS_DISPLAY,
} from '@/app/config/incident-status-display';

export type IncidentSeverity = 'critical' | 'high' | 'medium' | 'low';
export { isOpenIncidentStatus, openIncidentStatuses, resolvedIncidentStatuses };
export type { IncidentStatus };

export const incidentStatusConfig = Object.fromEntries(
  (
    Object.entries(STATUS_DISPLAY) as Array<
      [IncidentStatus, (typeof STATUS_DISPLAY)[IncidentStatus]]
    >
  ).map(([status, config]) => [
    status,
    {
      color: config.color,
      order: config.order,
      icon: config.icon,
      group: getIncidentStatusGroup(status),
    },
  ]),
) as Record<
  IncidentStatus,
  { color: string; order: number; icon: string; group: 'open' | 'resolved' }
>;

export const demoReferenceTimeIso = '2026-04-16T12:00:00Z';

export interface DemoIncident {
  id: string;
  code: string;
  identifier?: string;
  stationId: string | null;
  stationName: string | null;
  location: string | null;
  severity: IncidentSeverity;
  status: IncidentStatus;
  availableTransitions?: IncidentStatus[];
  resolutionType?: ResolutionType | null;
  blockingReason?: BlockingReason | null;
  title: string;
  aiSummary: string;
  /** Sources of the incident (e.g. AI diagnosis, Hotline Technique). */
  origin?: string[];
  createdAt: string;
  updatedAt: string;
  openedAt?: string | null;
  affectedChargers: number;
  totalChargers: number;
  /** Number of charging sessions successfully started on first plug-in attempt. */
  successfulStarts: number;
  /** Total number of plug-in / session-start attempts. */
  totalStarts: number;
  primaryAsset?: {
    id: string;
    name: string;
    serialNumber: string;
  } | null;
}

export interface TimelineEntry {
  time: string;
  source: 'external' | 'machine-log' | 'client-complaint';
  sourceLabel: string;
  title: string;
  description: string;
  rawCode?: string;
  translatedCode?: string;
  normalizedState?: string;
  stateConflict?: boolean;
}

export interface ConcernedLog {
  logId: string;
  receivedAt: string | null;
  isTreated: boolean;
  createdAt: string;
  rawLog: unknown | null;
}

export interface AIDiagnostics {
  incidentSummary: string;
  rootCauseAnalysis: string;
  recommendedRemoteActions?: {
    id: string;
    description: string;
  }[];
  armExecutionCommands?: {
    commandId: string;
    command?: string;
    actionId?: string;
    createdAt?: string;
    operation?: string;
    protocol?: string;
    remediationPlanId?: string;
    status: 'SUCCESS' | 'FAILED' | 'PENDING';
    targetAssetId?: string;
  }[];
  remoteActions: {
    action: string;
    status: 'SUCCESS' | 'FAILED' | 'PENDING';
    timestamp: string;
  }[];
  recommendedOnSiteActions: string[];
  estimatedResolutionTime: string;
  partsRequired: { partNumber: string; description: string }[];
  latestDiagnosisSnapshot?: Record<string, unknown> | null;
  /** ISO time of the delayed automatic diagnosis, null when none is scheduled. */
  autoDiagnosisDueAt?: string | null;
  /** Diagnostic rules given to the latest diagnosis (title null = EVIMO rule). */
  rulesUsed?: IncidentRuleUsed[];
  /** A rules search failed: the diagnosis ran without those rules. */
  rulesUnavailable?: boolean;
}

export interface ConnectorSnapshot {
  code: string;
  name: string;
  connectorType: 'CCS2' | 'AC Type 2';
  status: 'faulted' | 'available' | 'derated' | 'offline';
  health: 'poor' | 'fair' | 'good';
  powerKw: number;
  note: string;
}

export interface ExternalApiCheckSnapshot {
  requestedAt: string;
  weatherCheck: string;
  gridCheck: string;
  weatherResponse: {
    provider: string;
    station: string;
    observedTempC: number;
    alertLevel: string;
    impactAssessment: string;
  };
  gridResponse: {
    provider: string;
    region: string;
    feeder: string;
    declaredOutage: boolean;
    voltageDeltaPct: number;
    message: string;
  };
}

export interface IncidentDetail {
  incident: DemoIncident;
  timeline: TimelineEntry[];
  diagnostics: AIDiagnostics;
  connectors: ConnectorSnapshot[];
  concernedLogs: ConcernedLog[];
  externalApiChecks: ExternalApiCheckSnapshot[];
}

// ---------------------------------------------------------------------------
// State 1 — Global Dashboard: list of incidents, sorted by severity
// ---------------------------------------------------------------------------
const demoReferenceTimeMs = Date.parse(demoReferenceTimeIso);

function isoFromReferenceHours(hoursAgo: number): string {
  return new Date(
    demoReferenceTimeMs - Math.round(hoursAgo * 60 * 60 * 1000),
  ).toISOString();
}

function shiftIso(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}

function formatGeneratedIncidentId(incidentNumber: number): string {
  return `INC-2026-${String(incidentNumber).padStart(4, '0')}`;
}

const stationTemplates = [
  {
    stationId: 'STN-NCE-031',
    stationName: 'Nice Promenade — Seafront Hub',
    location: '06000 Nice, Quai des Etats-Unis',
  },
  {
    stationId: 'STN-LIL-028',
    stationName: 'Lille Europe — Parking Indigo',
    location: '59777 Lille, Avenue Le Corbusier',
  },
  {
    stationId: 'STN-MTP-012',
    stationName: 'Montpellier Antigone — Esplanade',
    location: '34000 Montpellier, Place du Nombre d Or',
  },
  {
    stationId: 'STN-REN-019',
    stationName: 'Rennes Centre — Colombier',
    location: '35000 Rennes, Place du Colombier',
  },
  {
    stationId: 'STN-STR-026',
    stationName: 'Strasbourg Gare — Hall Ouest',
    location: '67000 Strasbourg, Boulevard de Metz',
  },
  {
    stationId: 'STN-CLM-033',
    stationName: 'Clermont Ferrand — Jaude',
    location: '63000 Clermont-Ferrand, Rue Blatin',
  },
  {
    stationId: 'STN-ANG-017',
    stationName: 'Angers Ralliement — Centre Ville',
    location: '49000 Angers, Rue d Alsace',
  },
  {
    stationId: 'STN-GRE-022',
    stationName: 'Grenoble Presqu Ile — Hub B2',
    location: '38000 Grenoble, Avenue des Martyrs',
  },
  {
    stationId: 'STN-DIJ-016',
    stationName: 'Dijon Darcy — Parking Nord',
    location: '21000 Dijon, Place Darcy',
  },
  {
    stationId: 'STN-LMN-025',
    stationName: 'Le Mans Gare — Visitor Deck',
    location: '72000 Le Mans, Boulevard Robert Jarry',
  },
  {
    stationId: 'STN-RMS-023',
    stationName: 'Reims Centre — Forum',
    location: '51100 Reims, Rue de Vesle',
  },
  {
    stationId: 'STN-BES-018',
    stationName: 'Besancon Chateaufarine — Retail Hub',
    location: '25000 Besancon, Rue Rene Char',
  },
] as const;

function buildGeneratedIncidents(options: {
  count: number;
  startIncidentNumber: number;
  status: IncidentStatus;
  severityCycle: IncidentSeverity[];
  titlePool: string[];
  summaryPool: string[];
  resolutionType?: ResolutionType | null;
  blockingReason?: BlockingReason | null;
  stationOffset?: number;
  repairHours?: number;
  getAgeHours: (index: number) => number;
  getTotalChargers?: (index: number) => number;
  getAffectedChargers?: (index: number, totalChargers: number) => number;
}): DemoIncident[] {
  return Array.from({ length: options.count }, (_, index) => {
    const station =
      stationTemplates[
        (index + (options.stationOffset ?? 0)) % stationTemplates.length
      ]!;
    const totalChargers = options.getTotalChargers?.(index) ?? 6;
    const affectedChargers = Math.min(
      options.getAffectedChargers?.(index, totalChargers) ?? 0,
      totalChargers,
    );
    const createdAt = isoFromReferenceHours(options.getAgeHours(index));
    const repairHours = options.repairHours ?? 1.5;
    const updatedAt = isoFromReferenceHours(
      Math.max(options.getAgeHours(index) - repairHours, 0.25),
    );
    const totalStarts = totalChargers * 20;
    const healthRatio =
      (totalChargers - affectedChargers) / Math.max(totalChargers, 1);
    const successfulStarts = Math.round(
      totalStarts * (healthRatio * 0.97 + (1 - healthRatio) * 0.03),
    );

    return {
      id: formatGeneratedIncidentId(options.startIncidentNumber - index),
      code: formatGeneratedIncidentId(options.startIncidentNumber - index),
      stationId: station.stationId,
      stationName: station.stationName,
      location: station.location,
      severity: options.severityCycle[index % options.severityCycle.length]!,
      status: options.status,
      resolutionType: options.resolutionType ?? null,
      blockingReason: options.blockingReason ?? null,
      title: options.titlePool[index % options.titlePool.length]!,
      aiSummary: options.summaryPool[index % options.summaryPool.length]!,
      createdAt,
      updatedAt,
      affectedChargers,
      totalChargers,
      successfulStarts,
      totalStarts,
    };
  });
}

const baseDemoIncidents: DemoIncident[] = [
  {
    id: 'INC-2026-0871',
    code: 'INC-2026-0871',
    stationId: 'STN-LYN-042',
    stationName: 'Lyon Confluence — Hub A3',
    location: '69002 Lyon, Place Nautique',
    severity: 'critical',
    status: 'RCA_IN_PROGRESS',
    title: 'Inverter Safety Relay Trip — Full Station Offline',
    aiSummary:
      'Ambient heatwave (38 °C) combined with a 5 % grid voltage drop caused the inverter safety relay to trip on 4 of 6 chargers. All DC fast-charge points are currently offline.',
    createdAt: isoFromReferenceHours(8),
    updatedAt: isoFromReferenceHours(6.5),
    affectedChargers: 4,
    totalChargers: 6,
    successfulStarts: 41,
    totalStarts: 120,
  },
  {
    id: 'INC-2026-0870',
    code: 'INC-2026-0870',
    stationId: 'STN-PAR-118',
    stationName: 'Paris La Défense — Parking P2',
    location: '92400 Courbevoie, Espl. du Général de Gaulle',
    severity: 'high',
    status: 'RCA_IN_PROGRESS',
    title: 'Intermittent RFID Auth Failure',
    aiSummary:
      'RFID module firmware v2.1.3 is causing intermittent authentication timeouts (>3 s). 12 failed sessions in last hour. Firmware patch v2.1.4 is available.',
    createdAt: isoFromReferenceHours(30),
    updatedAt: isoFromReferenceHours(27.5),
    affectedChargers: 2,
    totalChargers: 8,
    successfulStarts: 118,
    totalStarts: 160,
  },
  {
    id: 'INC-2026-0869',
    code: 'INC-2026-0869',
    stationId: 'STN-MRS-007',
    stationName: 'Marseille Vieux-Port — Quai Rive Neuve',
    location: '13007 Marseille, Quai de Rive Neuve',
    severity: 'high',
    status: 'RCA_IN_PROGRESS',
    title: 'Ground Fault Detected on CCS Connector',
    aiSummary:
      'Ground-fault current (32 mA) detected during CCS handshake on Bay #3. Connector auto-locked. Likely moisture ingress after overnight rain.',
    createdAt: isoFromReferenceHours(46),
    updatedAt: isoFromReferenceHours(42),
    affectedChargers: 1,
    totalChargers: 4,
    successfulStarts: 59,
    totalStarts: 80,
  },
  {
    id: 'INC-2026-0868',
    code: 'INC-2026-0868',
    stationId: 'STN-TLS-021',
    stationName: 'Toulouse Blagnac — Terminal Parking',
    location: '31700 Blagnac, Aéroport',
    severity: 'medium',
    status: 'RCA_IN_PROGRESS',
    title: 'Degraded Charging Speed (22 kW cap)',
    aiSummary:
      'Power module #2 reporting thermal throttling. Charging speed capped at 22 kW instead of rated 50 kW. Cooling fan RPM nominal — heat sink paste may need replacement.',
    createdAt: isoFromReferenceHours(160),
    updatedAt: isoFromReferenceHours(157),
    affectedChargers: 1,
    totalChargers: 6,
    successfulStarts: 98,
    totalStarts: 120,
  },
  {
    id: 'INC-2026-0867',
    code: 'INC-2026-0867',
    stationId: 'STN-NTE-014',
    stationName: 'Nantes Commerce — Parking Decré',
    location: '44000 Nantes, Rue du Moulin',
    severity: 'medium',
    status: 'RCA_IN_PROGRESS',
    title: 'Display Panel Unresponsive',
    aiSummary:
      'Touchscreen HMI on Bay #1 not responding to input. Charger functional via mobile app only. Display controller logs show watchdog timeout (Err_0x2F).',
    createdAt: isoFromReferenceHours(440),
    updatedAt: isoFromReferenceHours(437),
    affectedChargers: 1,
    totalChargers: 4,
    successfulStarts: 59,
    totalStarts: 80,
  },
  {
    id: 'INC-2026-0866',
    code: 'INC-2026-0866',
    stationId: 'STN-BDX-009',
    stationName: 'Bordeaux Mériadeck — Niveau -1',
    location: "33000 Bordeaux, Rue du Château d'Eau",
    severity: 'low',
    status: 'REMOTE_PROPOSED',
    title: 'Scheduled Firmware Update Pending',
    aiSummary:
      'Firmware v3.8.1 available for 3 AC chargers. Update scheduled for tonight 02:00. No user impact expected.',
    createdAt: isoFromReferenceHours(288),
    updatedAt: isoFromReferenceHours(222),
    affectedChargers: 0,
    totalChargers: 3,
    successfulStarts: 58,
    totalStarts: 60,
  },
];

const generatedUnderInvestigationIncidents = buildGeneratedIncidents({
  count: 1,
  startIncidentNumber: 865,
  status: 'RCA_IN_PROGRESS',
  severityCycle: ['high'],
  titlePool: ['Intermittent DC Contactor Drift'],
  summaryPool: [
    'DC contactor chatter is being correlated with cabinet telemetry before a field visit is confirmed.',
  ],
  stationOffset: 3,
  getAgeHours: () => 92,
  getTotalChargers: () => 6,
  getAffectedChargers: () => 1,
});

const generatedToBeAffectedIncidents = buildGeneratedIncidents({
  count: 2,
  startIncidentNumber: 864,
  status: 'REMOTE_PROPOSED',
  severityCycle: ['medium', 'low'],
  titlePool: [
    'Predictive Cooling Alert on Power Cabinet',
    'Voltage Instability Risk Forecast',
  ],
  summaryPool: [
    'Thermal drift suggests one cabinet is likely to degrade within the next maintenance cycle if load remains unchanged.',
    'Grid telemetry forecasts a feeder dip pattern that may affect session stability during peak demand later today.',
  ],
  stationOffset: 5,
  getAgeHours: (index) => [5, 19][index]!,
  getTotalChargers: (index) => [4, 8][index]!,
  getAffectedChargers: () => 0,
});

const generatedAutoRepairIncidents = buildGeneratedIncidents({
  count: 52,
  startIncidentNumber: 862,
  status: 'RESOLVED',
  resolutionType: 'AUTO_RESOLVED',
  severityCycle: ['high', 'medium', 'low', 'medium'],
  repairHours: 52,
  titlePool: [
    'Remote Session Recovery Completed',
    'Charger Controller Auto-Healed',
    'Transient Fault Auto-Cleared',
    'Smart Restart Restored Service',
  ],
  summaryPool: [
    'The platform applied an automated corrective workflow, cleared the transient fault state, and restored full charger availability without technician dispatch.',
    'AI remediation replayed the last known good controller profile and brought the station back online within minutes.',
    'A self-healing sequence retried the affected subsystem and confirmed healthy telemetry after the restart window.',
    'Automated recovery logic resolved the incident before customer impact escalated beyond a short interruption.',
  ],
  stationOffset: 1,
  getAgeHours: (index) => 72 + index * 18,
  getTotalChargers: (index) => [4, 6, 8, 10][index % 4]!,
  getAffectedChargers: () => 0,
});

const generatedResolvedRemotelyIncidents = buildGeneratedIncidents({
  count: 2,
  startIncidentNumber: 810,
  status: 'RESOLVED',
  resolutionType: 'FIXED_REMOTE',
  severityCycle: ['medium', 'low'],
  repairHours: 52,
  titlePool: [
    'Firmware Rollback Applied',
    'Remote Network Reconfiguration Completed',
  ],
  summaryPool: [
    'Support engineers completed the fix through remote firmware control and verified normal charging behavior after validation checks.',
    'A remote configuration update restored connectivity and cleared the incident without an on-site visit.',
  ],
  stationOffset: 7,
  getAgeHours: (index) => [38, 560][index]!,
  getTotalChargers: (index) => [6, 4][index]!,
  getAffectedChargers: () => 0,
});

const generatedResolvedOnsiteIncidents = buildGeneratedIncidents({
  count: 4,
  startIncidentNumber: 808,
  status: 'CLOSED',
  resolutionType: 'FIXED_FIELD',
  severityCycle: ['critical', 'high', 'medium', 'low'],
  repairHours: 52,
  titlePool: [
    'Cooling Fan Assembly Replaced',
    'Connector Harness Re-Seated',
    'Breaker Panel Reset On Site',
    'Display Controller Swapped',
  ],
  summaryPool: [
    'A field technician completed the hardware intervention on site and confirmed the station returned to nominal operation.',
    'On-site maintenance resolved the physical defect and post-repair checks cleared the asset for service.',
    'Technician intervention addressed the root hardware issue and restored full charger availability after validation.',
    'The incident required hands-on work at the station before telemetry stabilized and the asset was returned to service.',
  ],
  stationOffset: 9,
  getAgeHours: (index) => [144, 240, 760, 980][index]!,
  getTotalChargers: (index) => [8, 6, 4, 4][index]!,
  getAffectedChargers: () => 0,
});

// ---------------------------------------------------------------------------
// Historical incidents — monthly counts from sample data (2024-01 → 2026-01)
// Reference time: 2026-04-16T12:00:00Z
// Hour boundaries calculated from calendar month starts (UTC).
// ---------------------------------------------------------------------------
const historicalMonthBuckets: [number, number, number][] = [
  // [startHoursAgo (month-start, older), endHoursAgo (month-end, newer), count]
  [2532, 1788, 12], // 2026-01
  [3276, 2532, 11], // 2025-12
  [3996, 3276, 10], // 2025-11
  [4740, 3996, 9], // 2025-10
  [5460, 4740, 8], // 2025-09
  [6204, 5460, 7], // 2025-08
  [6948, 6204, 6], // 2025-07
  [7668, 6948, 5], // 2025-06
  [8412, 7668, 13], // 2025-05
  [9132, 8412, 17], // 2025-04
  [9876, 9132, 7], // 2025-03
  [10548, 9876, 18], // 2025-02
  [11292, 10548, 7], // 2025-01
  [12036, 11292, 13], // 2024-12
  [12756, 12036, 13], // 2024-11
  [13500, 12756, 19], // 2024-10
  [14220, 13500, 19], // 2024-09
  [14964, 14220, 14], // 2024-08
  [15708, 14964, 14], // 2024-07
  [16428, 15708, 4], // 2024-06
  [17172, 16428, 12], // 2024-05
  [17892, 17172, 5], // 2024-04
  [18636, 17892, 7], // 2024-03
  [19332, 18636, 5], // 2024-02 (29 days — leap year)
  [20076, 19332, 6], // 2024-01
];

const historicalAgeHours: number[] = historicalMonthBuckets.flatMap(
  ([startH, endH, count]) =>
    Array.from({ length: count }, (_, i) =>
      Math.round(startH - ((startH - endH) * (i + 0.5)) / count),
    ),
);

const historicalIncidents = buildGeneratedIncidents({
  count: historicalAgeHours.length, // 261 total
  startIncidentNumber: 804,
  status: 'RESOLVED',
  resolutionType: 'AUTO_RESOLVED',
  severityCycle: ['high', 'medium', 'low', 'medium'],
  repairHours: 52,
  titlePool: [
    'Remote Session Recovery Completed',
    'Charger Controller Auto-Healed',
    'Transient Fault Auto-Cleared',
    'Smart Restart Restored Service',
  ],
  summaryPool: [
    'The platform applied an automated corrective workflow, cleared the transient fault state, and restored full charger availability without technician dispatch.',
    'AI remediation replayed the last known good controller profile and brought the station back online within minutes.',
    'A self-healing sequence retried the affected subsystem and confirmed healthy telemetry after the restart window.',
    'Automated recovery logic resolved the incident before customer impact escalated beyond a short interruption.',
  ],
  stationOffset: 2,
  getAgeHours: (index) => historicalAgeHours[index]!,
  getTotalChargers: (index) => [4, 6, 8, 10][index % 4]!,
  getAffectedChargers: () => 0,
});

export const demoIncidents: DemoIncident[] = [
  ...baseDemoIncidents,
  ...generatedUnderInvestigationIncidents,
  ...generatedToBeAffectedIncidents,
  ...generatedAutoRepairIncidents,
  ...generatedResolvedRemotelyIncidents,
  ...generatedResolvedOnsiteIncidents,
  ...historicalIncidents,
];

const demoIncidentsById = Object.fromEntries(
  demoIncidents.map((incident) => [incident.id, incident]),
) as Record<string, DemoIncident>;

// ---------------------------------------------------------------------------
// State 2 — Incident Detail: Timeline + AI Diagnostics for top incident
// ---------------------------------------------------------------------------
export const demoIncidentDetail: IncidentDetail = {
  incident: demoIncidentsById['INC-2026-0871']!,
  timeline: [
    {
      time: '2026-04-13T13:00:00Z',
      source: 'external',
      sourceLabel: 'Weather API + Enedis Grid API',
      title: 'Environmental Alert — Heatwave & Voltage Drop',
      description:
        'Météo-France reports 38 °C ambient temperature in Lyon 2e (heatwave alert level Orange). Simultaneously, the Enedis Distribution API reports a 5 % voltage sag on feeder LYN-HTA-42, affecting the Confluence district.',
    },
    {
      time: '2026-04-13T13:15:00Z',
      source: 'machine-log',
      sourceLabel: 'Charger OCPP Logs',
      title: 'Internal Temperature Warning Triggered',
      description:
        'Chargers #1–#4 reported StatusNotification with vendorErrorCode. Internal cabinet temperature exceeded 58 °C threshold. Power modules began thermal derating.',
      rawCode: 'Err_0x4A',
      translatedCode: 'Inverter Overheat — Safety Relay Pre-Trip Warning',
    },
    {
      time: '2026-04-13T13:30:00Z',
      source: 'machine-log',
      sourceLabel: 'Charger Session Logs',
      title: 'Charging Session Failures Recorded on Bays #1-#4',
      description:
        'OCPP transaction logs show repeated session start failures on connectors A3-C1 through A3-C4. Each attempt ended with StatusNotification=Faulted, relay_open=true, and chargerAvailability=Inoperative immediately after handshake.',
    },
  ],
  diagnostics: {
    incidentSummary:
      'Station Lyon Confluence Hub A3 experienced a cascading failure beginning at 13:00 on 13 April 2026. A combination of extreme ambient heat (38 °C) and a 5 % voltage drop on the local Enedis grid caused the inverter safety relays to trip on 4 of 6 DC fast-chargers. The station is currently 67 % offline.',
    rootCauseAnalysis:
      "The root cause is a dual environmental trigger: (1) Ambient temperature exceeded the inverter's 55 °C internal threshold, initiating thermal derating. (2) The concurrent Enedis grid voltage sag (5 %) pushed the inverter input below the minimum operating window (360 V DC bus), tripping the safety relay (Err_0x4A → Inverter Overheat). The cooling fans were operational but insufficient under combined stress. This matches a known failure pattern for ABB Terra 184 units under simultaneous heat + grid stress.",
    remoteActions: [
      {
        action:
          'Attempted remote soft-reset via OCPP RemoteReset command on Chargers #1–#4.',
        status: 'FAILED',
        timestamp: '2026-04-13T13:35:00Z',
      },
      {
        action:
          'Attempted to derate output to 22 kW via OCPP SetChargingProfile.',
        status: 'FAILED',
        timestamp: '2026-04-13T13:38:00Z',
      },
      {
        action:
          'Sent StatusNotification override to mark Chargers #5–#6 (AC) as Available.',
        status: 'SUCCESS',
        timestamp: '2026-04-13T13:40:00Z',
      },
    ],
    recommendedOnSiteActions: [
      'Dispatch technician to reset main breaker and inspect cooling fan assembly (Part #CF-204).',
      'Verify inverter DC bus voltage returns above 360 V after grid stabilisation.',
      'Apply thermal paste on heat sink contact surfaces if degradation is visible.',
      'Run OCPP diagnostic sequence post-restart to confirm all 4 chargers return to "Available" status.',
    ],
    estimatedResolutionTime: '45 – 60 min on-site',
    partsRequired: [
      {
        partNumber: 'CF-204',
        description: 'Cooling Fan Assembly — ABB Terra 184',
      },
      {
        partNumber: 'TP-112',
        description: 'Thermal Paste Compound (industrial grade)',
      },
    ],
  },
  connectors: [
    {
      code: 'A3-C1',
      name: 'Bay 1 - CCS2',
      connectorType: 'CCS2',
      status: 'faulted',
      health: 'poor',
      powerKw: 150,
      note: 'Safety relay tripped after inverter overheat warning Err_0x4A.',
    },
    {
      code: 'A3-C2',
      name: 'Bay 2 - CCS2',
      connectorType: 'CCS2',
      status: 'faulted',
      health: 'poor',
      powerKw: 150,
      note: 'Cabinet temperature exceeded 58 C. Session start blocked.',
    },
    {
      code: 'A3-C3',
      name: 'Bay 3 - CCS2',
      connectorType: 'CCS2',
      status: 'faulted',
      health: 'fair',
      powerKw: 150,
      note: 'Display black screen reported by driver. OCPP heartbeat still present.',
    },
    {
      code: 'A3-C4',
      name: 'Bay 4 - CCS2',
      connectorType: 'CCS2',
      status: 'faulted',
      health: 'poor',
      powerKw: 150,
      note: 'DC bus voltage below minimum operating window after grid sag.',
    },
    {
      code: 'A3-C5',
      name: 'Bay 5 - AC Type 2',
      connectorType: 'AC Type 2',
      status: 'available',
      health: 'good',
      powerKw: 22,
      note: 'Operational. Remotely confirmed available after status override.',
    },
    {
      code: 'A3-C6',
      name: 'Bay 6 - AC Type 2',
      connectorType: 'AC Type 2',
      status: 'available',
      health: 'good',
      powerKw: 22,
      note: 'Operational. Charging sessions accepted normally.',
    },
  ],
  concernedLogs: [],
  externalApiChecks: [
    {
      requestedAt: '2026-04-13T12:58:00Z',
      weatherCheck:
        'No significant temperature excursion could affect the connectors before 13:00. Ambient conditions remain within standard operating range.',
      gridCheck:
        'Querying Enedis: "pas de coupure de courant declaree dans la region". A local feeder dip is nevertheless visible on LYN-HTA-42.',
      weatherResponse: {
        provider: 'Meteo-France',
        station: 'Lyon 2e - Confluence',
        observedTempC: 31,
        alertLevel: 'Green',
        impactAssessment:
          'No significant thermal impact expected on connectors.',
      },
      gridResponse: {
        provider: 'Enedis',
        region: 'Auvergne-Rhone-Alpes',
        feeder: 'LYN-HTA-42',
        declaredOutage: false,
        voltageDeltaPct: -2,
        message: 'Pas de coupure de courant declaree dans la region.',
      },
    },
    {
      requestedAt: '2026-04-13T13:00:00Z',
      weatherCheck:
        'Heatwave conditions are now confirmed. Ambient temperature is high enough to increase inverter cabinet stress under sustained load.',
      gridCheck:
        'Enedis still reports no declared outage in the region, but telemetry shows a 5% voltage sag on feeder LYN-HTA-42.',
      weatherResponse: {
        provider: 'Meteo-France',
        station: 'Lyon 2e - Confluence',
        observedTempC: 38,
        alertLevel: 'Orange',
        impactAssessment:
          'Elevated thermal risk for power electronics and connector availability.',
      },
      gridResponse: {
        provider: 'Enedis',
        region: 'Auvergne-Rhone-Alpes',
        feeder: 'LYN-HTA-42',
        declaredOutage: false,
        voltageDeltaPct: -5,
        message:
          'Pas de coupure de courant declaree dans la region. Anomalie locale detectee sur le feeder LYN-HTA-42.',
      },
    },
    {
      requestedAt: '2026-04-13T13:06:00Z',
      weatherCheck:
        'Temperature remains critical with no short-term cooling expected. Connector-side derating risk persists.',
      gridCheck:
        'Regional outage status remains clear, but feeder voltage has not recovered and remains below nominal threshold.',
      weatherResponse: {
        provider: 'Meteo-France',
        station: 'Lyon 2e - Confluence',
        observedTempC: 39,
        alertLevel: 'Orange',
        impactAssessment:
          'Sustained heat load likely to aggravate inverter and connector stress.',
      },
      gridResponse: {
        provider: 'Enedis',
        region: 'Auvergne-Rhone-Alpes',
        feeder: 'LYN-HTA-42',
        declaredOutage: false,
        voltageDeltaPct: -5,
        message:
          'Aucune coupure declaree. Sous-tension locale persistante observee sur le feeder LYN-HTA-42.',
      },
    },
  ],
};

function buildConnectorSnapshots(options: {
  prefix: string;
  total: number;
  affected: number;
  affectedStatus: ConnectorSnapshot['status'];
  affectedHealth: ConnectorSnapshot['health'];
  affectedType: ConnectorSnapshot['connectorType'];
  healthyType: ConnectorSnapshot['connectorType'];
  affectedPowerKw: number;
  healthyPowerKw: number;
  affectedNote: string;
  healthyNote: string;
}): ConnectorSnapshot[] {
  return Array.from({ length: options.total }, (_, index) => {
    const bayNumber = index + 1;
    const isAffected = bayNumber <= options.affected;

    return {
      code: `${options.prefix}-C${bayNumber}`,
      name: `Bay ${bayNumber} - ${
        isAffected ? options.affectedType : options.healthyType
      }`,
      connectorType: isAffected ? options.affectedType : options.healthyType,
      status: isAffected ? options.affectedStatus : 'available',
      health: isAffected ? options.affectedHealth : 'good',
      powerKw: isAffected ? options.affectedPowerKw : options.healthyPowerKw,
      note: isAffected ? options.affectedNote : options.healthyNote,
    };
  });
}

function buildExternalApiChecks(options: {
  requestedAt: string[];
  weatherChecks: string[];
  gridChecks: string[];
  station: string;
  alertLevels: string[];
  temps: number[];
  impactAssessments: string[];
  region: string;
  feeder: string;
  voltageDeltas: number[];
  gridMessages: string[];
}): ExternalApiCheckSnapshot[] {
  return options.requestedAt.map((requestedAt, index) => ({
    requestedAt,
    weatherCheck: options.weatherChecks[index]!,
    gridCheck: options.gridChecks[index]!,
    weatherResponse: {
      provider: 'Meteo-France',
      station: options.station,
      observedTempC: options.temps[index]!,
      alertLevel: options.alertLevels[index]!,
      impactAssessment: options.impactAssessments[index]!,
    },
    gridResponse: {
      provider: 'Enedis',
      region: options.region,
      feeder: options.feeder,
      declaredOutage: false,
      voltageDeltaPct: options.voltageDeltas[index]!,
      message: options.gridMessages[index]!,
    },
  }));
}

export const demoIncidentDetailsById: Record<string, IncidentDetail> = {
  [demoIncidentDetail.incident.id]: demoIncidentDetail,
  'INC-2026-0870': {
    incident: demoIncidentsById['INC-2026-0870']!,
    timeline: [
      {
        time: '2026-04-13T12:02:00Z',
        source: 'external',
        sourceLabel: 'Weather API + Enedis Grid API',
        title: 'No External Disturbance Detected',
        description:
          'Weather and grid conditions are nominal around Paris La Defense. No temperature spike or declared regional grid event explains the authentication failures.',
      },
      {
        time: '2026-04-13T12:07:00Z',
        source: 'machine-log',
        sourceLabel: 'RFID Controller Logs',
        title: 'RFID Reader Timeout Pattern Observed',
        description:
          'Multiple authorization attempts exceed the controller timeout window immediately after badge read. Firmware logs point to a reader response stall on bays 1 and 2.',
        rawCode: 'Err_0x91',
        translatedCode: 'RFID Authentication Module Timeout',
      },
      {
        time: '2026-04-13T12:18:00Z',
        source: 'machine-log',
        sourceLabel: 'Charger Session Logs',
        title: 'Authorizations Rejected After Badge Read',
        description:
          'Session logs record 12 failed RFID transactions in 60 minutes. Mobile-app starts remain successful, isolating the issue to the badge-authentication path.',
      },
    ],
    diagnostics: {
      incidentSummary:
        'Paris La Defense Parking P2 is experiencing intermittent badge authentication failures on 2 of 8 chargers. App-based starts continue to work, limiting impact to on-site RFID sessions.',
      rootCauseAnalysis:
        'The incident is linked to RFID controller firmware v2.1.3. The reader process intermittently hangs after card scan, causing the charger to miss the authorization response deadline and reject the session.',
      remoteActions: [
        {
          action: 'Restarted RFID service on bays 1 and 2.',
          status: 'SUCCESS',
          timestamp: '2026-04-13T12:22:00Z',
        },
        {
          action: 'Pushed firmware rollback request from v2.1.3 to v2.1.2.',
          status: 'FAILED',
          timestamp: '2026-04-13T12:29:00Z',
        },
      ],
      recommendedOnSiteActions: [
        'Inspect RFID reader harness on bays 1 and 2.',
        'Apply firmware patch v2.1.4 during maintenance window.',
        'Validate badge scan latency after service restart.',
      ],
      estimatedResolutionTime: '30 - 45 min on-site',
      partsRequired: [
        {
          partNumber: 'RFID-17',
          description: 'RFID reader module',
        },
      ],
    },
    connectors: buildConnectorSnapshots({
      prefix: 'P2',
      total: 8,
      affected: 2,
      affectedStatus: 'derated',
      affectedHealth: 'fair',
      affectedType: 'AC Type 2',
      healthyType: 'AC Type 2',
      affectedPowerKw: 22,
      healthyPowerKw: 22,
      affectedNote:
        'RFID starts fail intermittently. Remote app start still available.',
      healthyNote: 'Connector operating normally.',
    }),
    concernedLogs: [],
    externalApiChecks: buildExternalApiChecks({
      requestedAt: ['2026-04-13T12:01:00Z', '2026-04-13T12:12:00Z'],
      weatherChecks: [
        'No significant temperature could affect the connectors or reader hardware.',
        'Ambient conditions remain stable with no weather-driven stress indicators.',
      ],
      gridChecks: [
        'Querying Enedis: no declared outage or instability in the region.',
        'Grid conditions remain nominal with no feeder anomaly associated with the site.',
      ],
      station: 'Paris La Defense - Parking P2',
      alertLevels: ['Green', 'Green'],
      temps: [22, 23],
      impactAssessments: [
        'No weather impact expected.',
        'No weather impact expected.',
      ],
      region: 'Ile-de-France',
      feeder: 'PAR-LDF-18',
      voltageDeltas: [0, 0],
      gridMessages: [
        'Aucune coupure declaree dans la region.',
        'Aucune anomalie reseau declaree dans la region.',
      ],
    }),
  },
  'INC-2026-0869': {
    incident: demoIncidentsById['INC-2026-0869']!,
    timeline: [
      {
        time: '2026-04-13T08:20:00Z',
        source: 'external',
        sourceLabel: 'Weather API + Enedis Grid API',
        title: 'Humidity Spike After Overnight Rain',
        description:
          'No grid disturbance is detected in Marseille, but humidity remains elevated after overnight rainfall, increasing risk of moisture ingress on exposed connector hardware.',
      },
      {
        time: '2026-04-13T08:37:00Z',
        source: 'machine-log',
        sourceLabel: 'Residual Current Monitor',
        title: 'Ground Fault Lockout Triggered on Bay 3',
        description:
          'Residual current detection exceeded the protection threshold during CCS handshake. The connector was automatically locked out for safety.',
        rawCode: 'Err_0x33',
        translatedCode: 'Ground Fault Lockout',
      },
      {
        time: '2026-04-13T08:45:00Z',
        source: 'machine-log',
        sourceLabel: 'Charger Session Logs',
        title: 'Connector Auto-Lock Confirmed',
        description:
          'Bay 3 shows repeated start attempts followed by immediate safety interruption. Adjacent bays remain available and unaffected.',
      },
    ],
    diagnostics: {
      incidentSummary:
        'Marseille Vieux-Port has a single CCS connector isolated after a ground fault event during handshake. The station remains partially available with 3 of 4 bays unaffected.',
      rootCauseAnalysis:
        'The failure pattern is consistent with moisture ingress on the connector path or cable interface. No external grid event is involved. Protection logic correctly locked out the affected bay.',
      remoteActions: [
        {
          action: 'Attempted connector unlock and self-test on bay 3.',
          status: 'FAILED',
          timestamp: '2026-04-13T08:52:00Z',
        },
      ],
      recommendedOnSiteActions: [
        'Inspect CCS connector and cable seal for moisture ingress.',
        'Measure insulation resistance before re-enabling the bay.',
        'Replace connector assembly if contamination is visible.',
      ],
      estimatedResolutionTime: '45 min on-site',
      partsRequired: [
        {
          partNumber: 'CCS-309',
          description: 'CCS connector assembly',
        },
      ],
    },
    connectors: buildConnectorSnapshots({
      prefix: 'MRS',
      total: 4,
      affected: 1,
      affectedStatus: 'faulted',
      affectedHealth: 'poor',
      affectedType: 'CCS2',
      healthyType: 'CCS2',
      affectedPowerKw: 150,
      healthyPowerKw: 150,
      affectedNote: 'Ground fault protection active. Connector locked out.',
      healthyNote: 'Connector operating normally.',
    }),
    concernedLogs: [],
    externalApiChecks: buildExternalApiChecks({
      requestedAt: ['2026-04-13T08:18:00Z', '2026-04-13T08:42:00Z'],
      weatherChecks: [
        'Recent rainfall and elevated humidity could affect exposed connector insulation.',
        'Moisture risk remains elevated around the station hardware.',
      ],
      gridChecks: [
        'Querying Enedis: no declared outage in the region.',
        'Grid status remains nominal with no feeder anomaly detected.',
      ],
      station: 'Marseille Vieux-Port',
      alertLevels: ['Green', 'Green'],
      temps: [18, 19],
      impactAssessments: [
        'Humidity is the relevant external factor.',
        'Humidity remains the relevant external factor.',
      ],
      region: 'Provence-Alpes-Cote d Azur',
      feeder: 'MRS-VP-07',
      voltageDeltas: [0, 0],
      gridMessages: [
        'Pas de coupure declaree dans la region.',
        'Aucune anomalie de reseau declaree.',
      ],
    }),
  },
  'INC-2026-0868': {
    incident: demoIncidentsById['INC-2026-0868']!,
    timeline: [
      {
        time: '2026-04-13T09:05:00Z',
        source: 'external',
        sourceLabel: 'Weather API + Enedis Grid API',
        title: 'Warm Ambient Conditions, Stable Grid',
        description:
          'Ambient temperature is elevated but below critical threshold. Grid telemetry is stable, pointing investigation toward local thermal behavior inside the charger cabinet.',
      },
      {
        time: '2026-04-13T09:12:00Z',
        source: 'machine-log',
        sourceLabel: 'Power Module Logs',
        title: 'Thermal Throttling Triggered',
        description:
          'Power module 2 begins current limiting after repeated high-temperature readings on the heat sink assembly.',
        rawCode: 'Err_0x58',
        translatedCode: 'Power Module Thermal Throttling',
      },
      {
        time: '2026-04-13T09:15:00Z',
        source: 'machine-log',
        sourceLabel: 'Charger Session Logs',
        title: 'Charging Power Capped at 22 kW',
        description:
          'Sessions on the affected bay continue, but all charging profiles are capped at 22 kW instead of rated 50 kW.',
      },
    ],
    diagnostics: {
      incidentSummary:
        'Toulouse Blagnac is operating with one thermally constrained bay. Service remains available but at reduced charging speed on the affected charger.',
      rootCauseAnalysis:
        'The thermal throttling pattern is localized to one power module. Cooling fan RPM is nominal, which suggests degraded thermal transfer at the heat sink or paste interface rather than an airflow failure.',
      remoteActions: [
        {
          action:
            'Reduced output setpoint to 22 kW to stabilize cabinet temperature.',
          status: 'SUCCESS',
          timestamp: '2026-04-13T09:18:00Z',
        },
      ],
      recommendedOnSiteActions: [
        'Inspect heat sink contact and thermal compound on module 2.',
        'Run thermal camera inspection during load test.',
        'Replace power module if hotspot persists after reseating.',
      ],
      estimatedResolutionTime: '60 min on-site',
      partsRequired: [
        {
          partNumber: 'PM-502',
          description: '50 kW power module',
        },
      ],
    },
    connectors: buildConnectorSnapshots({
      prefix: 'TLS',
      total: 6,
      affected: 1,
      affectedStatus: 'derated',
      affectedHealth: 'fair',
      affectedType: 'CCS2',
      healthyType: 'CCS2',
      affectedPowerKw: 22,
      healthyPowerKw: 50,
      affectedNote:
        'Charging available at reduced power because of thermal throttling.',
      healthyNote: 'Connector operating at nominal power.',
    }),
    concernedLogs: [],
    externalApiChecks: buildExternalApiChecks({
      requestedAt: ['2026-04-13T09:03:00Z', '2026-04-13T09:14:00Z'],
      weatherChecks: [
        'Warm weather is present but not severe enough on its own to explain the full derating.',
        'Ambient heat contributes to cabinet load but is not the sole root cause.',
      ],
      gridChecks: [
        'No declared Enedis disturbance in the area.',
        'Grid remains stable with nominal feeder behavior.',
      ],
      station: 'Toulouse Blagnac',
      alertLevels: ['Yellow', 'Yellow'],
      temps: [29, 30],
      impactAssessments: [
        'Moderate thermal contribution.',
        'Moderate thermal contribution.',
      ],
      region: 'Occitanie',
      feeder: 'TLS-BLG-21',
      voltageDeltas: [0, 0],
      gridMessages: [
        'Aucune coupure declaree dans la region.',
        'Aucune anomalie reseau declaree.',
      ],
    }),
  },
  'INC-2026-0867': {
    incident: demoIncidentsById['INC-2026-0867']!,
    timeline: [
      {
        time: '2026-04-13T09:52:00Z',
        source: 'external',
        sourceLabel: 'Weather API + Enedis Grid API',
        title: 'External Conditions Normal',
        description:
          'No weather or grid anomaly is correlated with the HMI issue. The problem appears confined to the local display subsystem.',
      },
      {
        time: '2026-04-13T09:57:00Z',
        source: 'machine-log',
        sourceLabel: 'Display Controller Logs',
        title: 'Watchdog Timeout on HMI Controller',
        description:
          'The display process restarts repeatedly after watchdog expiration, leaving the local user interface unavailable.',
        rawCode: 'Err_0x2F',
        translatedCode: 'HMI Watchdog Timeout',
      },
      {
        time: '2026-04-13T10:04:00Z',
        source: 'machine-log',
        sourceLabel: 'Charger Session Logs',
        title: 'Remote Start Still Operational',
        description:
          'Backend sessions triggered from the mobile application continue successfully. Only the local touchscreen interaction path is affected.',
      },
    ],
    diagnostics: {
      incidentSummary:
        'Nantes Commerce has one bay with a failed local display, but charging remains technically available through remote-start channels.',
      rootCauseAnalysis:
        'The watchdog restart loop isolates the issue to the HMI controller stack rather than power electronics or grid conditions. The charger remains functional behind the display failure.',
      remoteActions: [
        {
          action: 'Attempted remote restart of the HMI service.',
          status: 'FAILED',
          timestamp: '2026-04-13T10:08:00Z',
        },
      ],
      recommendedOnSiteActions: [
        'Inspect HMI controller board and ribbon cable.',
        'Reflash display firmware if storage corruption is suspected.',
        'Replace display controller if watchdog loop persists.',
      ],
      estimatedResolutionTime: '30 min on-site',
      partsRequired: [
        {
          partNumber: 'HMI-221',
          description: 'Display controller board',
        },
      ],
    },
    connectors: buildConnectorSnapshots({
      prefix: 'NTE',
      total: 4,
      affected: 1,
      affectedStatus: 'faulted',
      affectedHealth: 'fair',
      affectedType: 'AC Type 2',
      healthyType: 'AC Type 2',
      affectedPowerKw: 22,
      healthyPowerKw: 22,
      affectedNote: 'Local screen unavailable. Remote-start remains possible.',
      healthyNote: 'Connector operating normally.',
    }),
    concernedLogs: [],
    externalApiChecks: buildExternalApiChecks({
      requestedAt: ['2026-04-13T09:50:00Z', '2026-04-13T10:02:00Z'],
      weatherChecks: [
        'No significant temperature or weather condition could affect the connectors.',
        'External conditions remain irrelevant to the display fault.',
      ],
      gridChecks: [
        'Querying Enedis: pas de coupure de courant declaree dans la region.',
        'No regional grid event or feeder anomaly is reported.',
      ],
      station: 'Nantes Commerce',
      alertLevels: ['Green', 'Green'],
      temps: [17, 18],
      impactAssessments: [
        'No weather impact expected.',
        'No weather impact expected.',
      ],
      region: 'Pays de la Loire',
      feeder: 'NTE-COM-14',
      voltageDeltas: [0, 0],
      gridMessages: [
        'Pas de coupure declaree dans la region.',
        'Aucune anomalie reseau declaree.',
      ],
    }),
  },
  'INC-2026-0866': {
    incident: demoIncidentsById['INC-2026-0866']!,
    timeline: [
      {
        time: '2026-04-12T16:05:00Z',
        source: 'external',
        sourceLabel: 'Weather API + Enedis Grid API',
        title: 'External Conditions Cleared for Update Window',
        description:
          'No weather or grid limitation is expected during the planned overnight maintenance window.',
      },
      {
        time: '2026-04-12T16:12:00Z',
        source: 'machine-log',
        sourceLabel: 'Update Orchestrator Logs',
        title: 'Firmware Package Staged Remotely',
        description:
          'Firmware version 3.8.1 has been downloaded and verified on the three AC chargers. Installation is queued for 02:00 local time.',
        rawCode: 'Pkg_3_8_1',
        translatedCode: 'Firmware Package Ready for Scheduled Deployment',
      },
      {
        time: '2026-04-13T08:00:00Z',
        source: 'machine-log',
        sourceLabel: 'Remote Maintenance Logs',
        title: 'Remote Preparation Completed',
        description:
          'Pre-checks passed and no on-site intervention is required. Incident is marked resolved remotely pending the scheduled rollout.',
      },
    ],
    diagnostics: {
      incidentSummary:
        'Bordeaux Meriadeck has no active user-facing fault. The platform identified a pending firmware update and prepared the site for a remote overnight rollout.',
      rootCauseAnalysis:
        'This is a maintenance opportunity rather than a service outage. The incident remains informational and is already resolved operationally through remote preparation.',
      remoteActions: [
        {
          action: 'Downloaded and validated firmware package v3.8.1.',
          status: 'SUCCESS',
          timestamp: '2026-04-12T16:12:00Z',
        },
        {
          action: 'Scheduled remote deployment for 02:00 local time.',
          status: 'SUCCESS',
          timestamp: '2026-04-12T16:18:00Z',
        },
      ],
      recommendedOnSiteActions: ['No on-site action required.'],
      estimatedResolutionTime: 'Resolved remotely',
      partsRequired: [],
    },
    connectors: buildConnectorSnapshots({
      prefix: 'BDX',
      total: 3,
      affected: 0,
      affectedStatus: 'available',
      affectedHealth: 'good',
      affectedType: 'AC Type 2',
      healthyType: 'AC Type 2',
      affectedPowerKw: 22,
      healthyPowerKw: 22,
      affectedNote: 'No active fault.',
      healthyNote: 'Ready for scheduled firmware update.',
    }),
    concernedLogs: [],
    externalApiChecks: buildExternalApiChecks({
      requestedAt: ['2026-04-12T16:02:00Z', '2026-04-13T07:55:00Z'],
      weatherChecks: [
        'No significant weather condition could affect the overnight deployment window.',
        'Weather remains suitable for unattended remote maintenance.',
      ],
      gridChecks: [
        'Querying Enedis: no declared outage in the region.',
        'Grid conditions remain stable for the scheduled remote update.',
      ],
      station: 'Bordeaux Meriadeck',
      alertLevels: ['Green', 'Green'],
      temps: [16, 15],
      impactAssessments: [
        'No weather impact expected.',
        'No weather impact expected.',
      ],
      region: 'Nouvelle-Aquitaine',
      feeder: 'BDX-MER-09',
      voltageDeltas: [0, 0],
      gridMessages: [
        'Aucune coupure declaree dans la region.',
        'Aucune anomalie reseau declaree.',
      ],
    }),
  },
};

function buildGenericIncidentDetail(incident: DemoIncident): IncidentDetail {
  const isOpen = isOpenIncidentStatus(incident.status);
  const isPredicted = incident.status === 'REMOTE_PROPOSED';
  const isAutoRepair = incident.resolutionType === 'AUTO_RESOLVED';
  const isRemoteResolved =
    incident.resolutionType === 'FIXED_REMOTE' || isAutoRepair;
  const isOnsiteResolved = incident.resolutionType === 'FIXED_FIELD';
  const affectedConnectorCount = isPredicted
    ? Math.max(1, incident.affectedChargers)
    : isOpen
      ? incident.affectedChargers
      : 0;
  const connectorPrefix =
    incident.stationId?.split('-').at(-1) ?? incident.stationId ?? 'STN';
  const incidentLabel = incident.status.toLowerCase().replace(/_/g, ' ');

  const followUpAction = isOnsiteResolved
    ? 'Validated repair and released the station back to operations.'
    : isRemoteResolved
      ? 'Applied the remote correction and verified station recovery.'
      : isAutoRepair
        ? 'Executed the automated repair playbook and confirmed healthy telemetry.'
        : isPredicted
          ? 'Raised a proactive watchlist item so operations can intervene before service degradation.'
          : 'Continued AI-guided diagnosis while the operations team reviews escalation options.';

  return {
    incident,
    timeline: [
      {
        time: shiftIso(incident.createdAt, -18),
        source: 'external',
        sourceLabel: 'Weather API + Enedis Grid API',
        title: isPredicted
          ? 'Pre-Incident Risk Signal Elevated'
          : 'Contextual Signal Captured',
        description: isPredicted
          ? 'Forecasted operating conditions and recent station telemetry suggest a higher-than-normal probability of service degradation in the next few hours.'
          : 'Station context, telemetry, and environmental inputs were correlated when the incident pattern was first detected.',
      },
      {
        time: incident.createdAt,
        source: 'machine-log',
        sourceLabel: 'Station Telemetry',
        title: `${incidentLabel} Event Created`,
        description: incident.aiSummary,
        rawCode: isPredicted
          ? 'Pred_0x11'
          : isAutoRepair
            ? 'Auto_0x21'
            : 'Evt_0x19',
        translatedCode: isPredicted
          ? 'Predictive Maintenance Flag'
          : isAutoRepair
            ? 'Automatic Recovery Sequence'
            : 'Incident Correlation Event',
      },
      {
        time: incident.updatedAt,
        source: isOnsiteResolved ? 'client-complaint' : 'machine-log',
        sourceLabel: isOnsiteResolved
          ? 'Technician Completion Report'
          : 'Operations Follow-Up',
        title: isOpen
          ? 'Latest Status Update Published'
          : 'Recovery Confirmation Recorded',
        description: followUpAction,
      },
    ],
    diagnostics: {
      incidentSummary: isOpen
        ? `${incident.stationName} is currently tracked as ${incidentLabel.toLowerCase()}. AI correlation is still validating the most likely fault domain before final dispatch decisions are taken.`
        : `${incident.stationName} was recovered with status ${incidentLabel.toLowerCase()}. The platform has retained the incident for auditability and trend analysis.`,
      rootCauseAnalysis: isPredicted
        ? 'The incident is currently predictive rather than customer-visible. Telemetry drift, prior maintenance history, and external conditions suggest elevated risk on the station, but the chargers remain available.'
        : isAutoRepair
          ? 'The pattern matched a known transient fault signature that the platform can remediate automatically. Automated correction succeeded and post-check telemetry returned to normal bounds.'
          : isRemoteResolved
            ? 'The root cause was isolated to a remotely correctable configuration or firmware issue. The operations workflow resolved the incident without field intervention.'
            : isOnsiteResolved
              ? 'The incident required a physical intervention at the station to restore service. Hardware or connector-side remediation was validated on site before closure.'
              : 'The incident remains active while AI correlation narrows the most likely subsystem and validates whether a remote action is still possible.',
      remoteActions: isOnsiteResolved
        ? [
            {
              action:
                'Prepared on-site intervention package for the dispatched technician.',
              status: 'SUCCESS',
              timestamp: shiftIso(incident.createdAt, 22),
            },
          ]
        : [
            {
              action: isPredicted
                ? 'Generated preventive advisory and escalated the asset watchlist.'
                : isAutoRepair
                  ? 'Executed automated corrective workflow.'
                  : isRemoteResolved
                    ? 'Applied remote remediation sequence.'
                    : 'Collected extended diagnostics for the active station.',
              status: isOpen && !isPredicted ? 'FAILED' : 'SUCCESS',
              timestamp: shiftIso(incident.createdAt, 18),
            },
          ],
      recommendedOnSiteActions: isOpen
        ? isPredicted
          ? [
              'Review the predictive alert and schedule a preventive site inspection.',
              'Inspect the highest-risk bay before the next demand peak.',
            ]
          : [
              'Inspect the affected bay and confirm subsystem health on site.',
              'Run the station diagnostic routine after corrective action.',
            ]
        : ['No additional on-site action required.'],
      estimatedResolutionTime: isOpen
        ? isPredicted
          ? 'Preventive intervention recommended within 24 hours'
          : 'Under active investigation'
        : isOnsiteResolved
          ? 'Resolved onsite'
          : 'Resolved remotely',
      partsRequired: isOnsiteResolved
        ? [
            {
              partNumber: 'ONS-101',
              description: 'Field replacement component validated on site',
            },
          ]
        : [],
    },
    connectors: buildConnectorSnapshots({
      prefix: connectorPrefix,
      total: incident.totalChargers,
      affected: affectedConnectorCount,
      affectedStatus: isPredicted ? 'derated' : 'faulted',
      affectedHealth: isPredicted ? 'fair' : 'poor',
      affectedType: 'CCS2',
      healthyType: 'AC Type 2',
      affectedPowerKw: isPredicted ? 50 : 22,
      healthyPowerKw: 22,
      affectedNote: isPredicted
        ? 'Connector remains available but is flagged by predictive monitoring.'
        : 'Connector requires further validation before it can be released.',
      healthyNote: isOpen
        ? 'Remaining connectors continue to operate within normal parameters.'
        : 'Connector has been verified after recovery.',
    }),
    concernedLogs: [],
    externalApiChecks: buildExternalApiChecks({
      requestedAt: [shiftIso(incident.createdAt, -12), incident.updatedAt],
      weatherChecks: [
        'Environmental conditions were checked against the station risk envelope and correlated with current telemetry.',
        'No external blocker remains after the latest status refresh.',
      ],
      gridChecks: [
        'Feeder telemetry and declared regional events were evaluated during incident creation.',
        'Grid-side context was refreshed as part of the latest incident review.',
      ],
      station: incident.stationName ?? 'Unknown station',
      alertLevels: [isPredicted ? 'Yellow' : 'Green', 'Green'],
      temps: [24, 23],
      impactAssessments: [
        isPredicted
          ? 'Conditions support a preventive intervention window.'
          : 'No major weather-derived impact was confirmed.',
        'Latest refresh shows stable external conditions.',
      ],
      region: 'France',
      feeder: `${connectorPrefix}-GRID`,
      voltageDeltas: [isPredicted ? -2 : 0, 0],
      gridMessages: [
        'No declared regional outage is associated with this station.',
        'Station context has been refreshed and published to the dashboard.',
      ],
    }),
  };
}

export function getDemoIncidentDetail(
  incidentId: string,
): IncidentDetail | undefined {
  return (
    demoIncidentDetailsById[incidentId] ??
    (demoIncidentsById[incidentId]
      ? buildGenericIncidentDetail(demoIncidentsById[incidentId])
      : undefined)
  );
}

// ---------------------------------------------------------------------------
// State 3 — Dispatch: generated work-order link
// ---------------------------------------------------------------------------
export const demoWorkOrderLink = 'mobile.app.com/work-order/8f92a';
